import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PlanfactOperation,
  getPlanfactOperations,
  syncPlanfactOperations,
  syncPlanfactOperationsChanges,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatContractSumDisplay,
  formatDateShort,
  formatDateTimeMsk,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { normalizeOperationAmount } from '../utils/operationBudgetArticle';
import '../styles/DataPage.css';

type OperationSortKey =
  | 'planfact_operation_id'
  | 'operation_date'
  | 'account_name'
  | 'account_type'
  | 'operation_type'
  | 'budget_article'
  | 'category_name'
  | 'amount'
  | 'cash_amount'
  | 'tax_amount'
  | 'serv_fee_amount'
  | 'project_name'
  | 'deal_name'
  | 'contractor_name_pf'
  | 'comment'
  | 'modified_at'
  | 'synced_at';

type OperationsFilterMenuKey =
  | 'account'
  | 'type'
  | 'category'
  | 'project'
  | 'deal'
  | 'contractor'
  | null;

const OPERATION_TYPE_LABELS: Record<string, string> = {
  Income: '←',
  Outcome: '→',
  Move: '↔',
  Accrual: 'нач.',
  Shipment: 'отгр.',
  Supply: 'пост.',
};

function formatOperationType(value?: string): string {
  if (!value) {
    return '—';
  }
  return OPERATION_TYPE_LABELS[value] ?? value;
}

function renderOperationType(value?: string): React.ReactNode {
  const label = formatOperationType(value);
  if (value === 'Income') {
    return <span className="operations-page__type operations-page__type--income">{label}</span>;
  }
  if (value === 'Outcome') {
    return <span className="operations-page__type operations-page__type--outcome">{label}</span>;
  }
  return <span className="operations-page__type">{label}</span>;
}

function formatOptionalAmount(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }
  return formatContractSumDisplay(value);
}

function parseAmountBound(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string, from: string, to: string): boolean {
  const hasFrom = from.trim() !== '';
  const hasTo = to.trim() !== '';
  if (!hasFrom && !hasTo) return true;

  const day = parseFilterDay(value);
  if (day === null) return true;

  let rangeStart = hasFrom ? parseFilterDay(from) : Number.NEGATIVE_INFINITY;
  let rangeEnd = hasTo ? parseFilterDay(to) : Number.POSITIVE_INFINITY;
  if (rangeStart === null) rangeStart = Number.NEGATIVE_INFINITY;
  if (rangeEnd === null) rangeEnd = Number.POSITIVE_INFINITY;
  return day >= rangeStart && day <= rangeEnd;
}

function datetimeInRange(value: string, from: string, to: string): boolean {
  const hasFrom = from.trim() !== '';
  const hasTo = to.trim() !== '';
  if (!hasFrom && !hasTo) return true;

  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return true;

  const fromTs = hasFrom ? new Date(`${from}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? new Date(`${to}T23:59:59`).getTime() : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

const OperationsPage: React.FC = () => {
  const { can } = useAuth();
  const [operations, setOperations] = useState<PlanfactOperation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [syncMode, setSyncMode] = useState<'full' | 'changes' | null>(null);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<OperationsFilterMenuKey>(null);
  const [searchText, setSearchText] = useState('');

  const [filterPfId, setFilterPfId] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSyncedFrom, setFilterSyncedFrom] = useState('');
  const [filterSyncedTo, setFilterSyncedTo] = useState('');
  const [filterAccounts, setFilterAccounts] = useState<string[]>([]);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterCategories, setFilterCategories] = useState<string[]>([]);
  const [filterProjects, setFilterProjects] = useState<string[]>([]);
  const [filterDeals, setFilterDeals] = useState<string[]>([]);
  const [filterContractors, setFilterContractors] = useState<string[]>([]);
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');

  const [sortState, setSortState] = useState<SortState<OperationSortKey>>({
    key: 'modified_at',
    direction: 'desc',
  });

  const canOpenPage = can('OPERATIONS_SYNC', 'EDIT');
  const canSync = canOpenPage;

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setOperations([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getPlanfactOperations();
      setOperations(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка при загрузке операций';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setOperations([]);
    }
  }, [canOpenPage, fetchData]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const uniqueValues = useCallback(
    (picker: (row: PlanfactOperation) => string | undefined): string[] => {
      const set = new Set<string>();
      for (const row of operations) {
        const value = picker(row)?.trim();
        if (value) {
          set.add(value);
        }
      }
      return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
    },
    [operations],
  );

  const accountOptions = useMemo(() => uniqueValues((row) => row.account_name), [uniqueValues]);
  const typeOptions = useMemo(() => uniqueValues((row) => row.operation_type), [uniqueValues]);
  const categoryOptions = useMemo(() => uniqueValues((row) => row.category_name), [uniqueValues]);
  const projectOptions = useMemo(() => uniqueValues((row) => row.project_name), [uniqueValues]);
  const dealOptions = useMemo(() => uniqueValues((row) => row.deal_name), [uniqueValues]);
  const contractorOptions = useMemo(() => uniqueValues((row) => row.contractor_name_pf), [uniqueValues]);

  const hasActiveFilters = useMemo(
    () =>
      filterPfId.trim() !== ''
      || filterDateFrom.trim() !== ''
      || filterDateTo.trim() !== ''
      || filterSyncedFrom.trim() !== ''
      || filterSyncedTo.trim() !== ''
      || filterAccounts.length > 0
      || filterTypes.length > 0
      || filterCategories.length > 0
      || filterProjects.length > 0
      || filterDeals.length > 0
      || filterContractors.length > 0
      || minAmountFilter.trim() !== ''
      || maxAmountFilter.trim() !== '',
    [
      filterPfId,
      filterDateFrom,
      filterDateTo,
      filterSyncedFrom,
      filterSyncedTo,
      filterAccounts,
      filterTypes,
      filterCategories,
      filterProjects,
      filterDeals,
      filterContractors,
      minAmountFilter,
      maxAmountFilter,
    ],
  );

  const clearFilters = (): void => {
    setFilterPfId('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSyncedFrom('');
    setFilterSyncedTo('');
    setFilterAccounts([]);
    setFilterTypes([]);
    setFilterCategories([]);
    setFilterProjects([]);
    setFilterDeals([]);
    setFilterContractors([]);
    setMinAmountFilter('');
    setMaxAmountFilter('');
  };

  const filteredOperations = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseAmountBound(minAmountFilter);
    const sumToN = parseAmountBound(maxAmountFilter);

    return operations.filter((row) => {
      const searchable = [
        row.planfact_operation_id,
        row.operation_date,
        row.account_name,
        row.account_type,
        row.operation_type,
        formatOperationType(row.operation_type),
        row.category_name,
        row.budget_article,
        row.project_name,
        row.deal_name,
        row.contractor_name_pf,
        row.comment,
        row.modified_at,
        row.synced_at,
        row.tax_amount != null ? String(row.tax_amount) : '',
        row.serv_fee_amount != null ? String(row.serv_fee_amount) : '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const amount = normalizeOperationAmount(row.amount, row.operation_type);

      return (
        (!text || searchable.includes(text))
        && (!filterPfId.trim() || row.planfact_operation_id.toLowerCase().includes(filterPfId.trim().toLowerCase()))
        && dateInRange(row.operation_date, filterDateFrom, filterDateTo)
        && datetimeInRange(row.synced_at, filterSyncedFrom, filterSyncedTo)
        && (filterAccounts.length === 0 || (row.account_name && filterAccounts.includes(row.account_name)))
        && (filterTypes.length === 0 || (row.operation_type && filterTypes.includes(row.operation_type)))
        && (filterCategories.length === 0 || (row.category_name && filterCategories.includes(row.category_name)))
        && (filterProjects.length === 0 || (row.project_name && filterProjects.includes(row.project_name)))
        && (filterDeals.length === 0 || (row.deal_name && filterDeals.includes(row.deal_name)))
        && (filterContractors.length === 0 || (row.contractor_name_pf && filterContractors.includes(row.contractor_name_pf)))
        && (sumFromN === null || amount >= sumFromN)
        && (sumToN === null || amount <= sumToN)
      );
    });
  }, [
    operations,
    searchText,
    filterPfId,
    filterDateFrom,
    filterDateTo,
    filterSyncedFrom,
    filterSyncedTo,
    filterAccounts,
    filterTypes,
    filterCategories,
    filterProjects,
    filterDeals,
    filterContractors,
    minAmountFilter,
    maxAmountFilter,
  ]);

  const sortedOperations = useMemo(() => {
    const sorted = [...filteredOperations];
    sorted.sort((left, right) => {
      const leftMap: Record<OperationSortKey, string | number | undefined> = {
        planfact_operation_id: left.planfact_operation_id,
        operation_date: left.operation_date,
        account_name: left.account_name,
        account_type: left.account_type,
        operation_type: left.operation_type,
        budget_article: left.budget_article,
        category_name: left.category_name,
        amount: normalizeOperationAmount(left.amount, left.operation_type),
        cash_amount: left.cash_amount,
        tax_amount: left.tax_amount,
        serv_fee_amount: left.serv_fee_amount,
        project_name: left.project_name,
        deal_name: left.deal_name,
        contractor_name_pf: left.contractor_name_pf,
        comment: left.comment,
        modified_at: left.modified_at,
        synced_at: left.synced_at,
      };
      const rightMap: Record<OperationSortKey, string | number | undefined> = {
        planfact_operation_id: right.planfact_operation_id,
        operation_date: right.operation_date,
        account_name: right.account_name,
        account_type: right.account_type,
        operation_type: right.operation_type,
        budget_article: right.budget_article,
        category_name: right.category_name,
        amount: normalizeOperationAmount(right.amount, right.operation_type),
        cash_amount: right.cash_amount,
        tax_amount: right.tax_amount,
        serv_fee_amount: right.serv_fee_amount,
        project_name: right.project_name,
        deal_name: right.deal_name,
        contractor_name_pf: right.contractor_name_pf,
        comment: right.comment,
        modified_at: right.modified_at,
        synced_at: right.synced_at,
      };
      return compareValues(leftMap[sortState.key], rightMap[sortState.key], sortState.direction);
    });
    return sorted;
  }, [filteredOperations, sortState]);

  const totalFilteredAmount = useMemo(
    () => sortedOperations.reduce(
      (sum, row) => sum + normalizeOperationAmount(row.amount, row.operation_type),
      0,
    ),
    [sortedOperations],
  );

  const totalFilteredCashAmount = useMemo(
    () => sortedOperations.reduce(
      (sum, row) => sum + (row.cash_amount ?? 0),
      0,
    ),
    [sortedOperations],
  );

  const totalFilteredTaxAmount = useMemo(
    () => sortedOperations.reduce(
      (sum, row) => sum + (row.tax_amount ?? 0),
      0,
    ),
    [sortedOperations],
  );

  const totalFilteredServFeeAmount = useMemo(
    () => sortedOperations.reduce(
      (sum, row) => sum + (row.serv_fee_amount ?? 0),
      0,
    ),
    [sortedOperations],
  );

  const handleSort = (key: OperationSortKey): void => {
    setSortState((current) => toggleSort(
      current,
      key,
      key === 'operation_date' || key === 'modified_at' || key === 'synced_at' ? 'desc' : 'asc',
    ));
  };

  const renderSortableHeader = (
    key: OperationSortKey,
    label: string,
    className = '',
  ): React.ReactNode => (
    <th
      className={`sortable ${className}`.trim()}
      onClick={() => handleSort(key)}
      role="columnheader"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleSort(key);
        }
      }}
      aria-sort={sortState.key === key ? (sortState.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sortState.key === key ? (sortState.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  const handleFullSync = async (): Promise<void> => {
    if (!canSync || syncMode !== null) {
      return;
    }
    setSyncMode('full');
    setError('');
    setSyncMessage('');
    try {
      const result = await syncPlanfactOperations();
      setSyncMessage(result.message);
      await fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка синхронизации';
      setError(message);
    } finally {
      setSyncMode(null);
    }
  };

  const handleChangesSync = async (): Promise<void> => {
    if (!canSync || syncMode !== null) {
      return;
    }
    setSyncMode('changes');
    setError('');
    setSyncMessage('');
    try {
      const result = await syncPlanfactOperationsChanges();
      setSyncMessage(result.message);
      await fetchData();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка синхронизации изменений';
      setError(message);
    } finally {
      setSyncMode(null);
    }
  };

  return (
    <div className="data-page operations-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице операций (требуется OPERATIONS_SYNC EDIT).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Операции</h1>
            {canSync ? (
              <div className="operations-page__sync-actions">
                <button
                  type="button"
                  className="create-button operations-page__sync-button"
                  onClick={() => void handleChangesSync()}
                  disabled={syncMode !== null}
                >
                  {syncMode === 'changes' ? 'Синхронизация…' : 'Синхр. изменений'}
                </button>
                <button
                  type="button"
                  className="filters-toggle operations-page__sync-button"
                  onClick={() => void handleFullSync()}
                  disabled={syncMode !== null}
                >
                  {syncMode === 'full' ? 'Синхронизация…' : 'Синхр. полная'}
                </button>
              </div>
            ) : null}
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error ? <div className="error-message">{error}</div> : null}
          {syncMessage ? <div className="operations-page__sync-message">{syncMessage}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen ? (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <label>
                    ID в ПФ
                    <input value={filterPfId} onChange={(e) => setFilterPfId(e.target.value)} />
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Счёт"
                      options={accountOptions}
                      selected={filterAccounts}
                      onSelectedChange={setFilterAccounts}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'account'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'account' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Тип"
                      options={typeOptions}
                      selected={filterTypes}
                      onSelectedChange={setFilterTypes}
                      formatOption={(v) => formatOperationType(v)}
                      isOpen={filterMenuOpen === 'type'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'type' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Контрагент"
                      options={contractorOptions}
                      selected={filterContractors}
                      onSelectedChange={setFilterContractors}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'contractor'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'contractor' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статья"
                      options={categoryOptions}
                      selected={filterCategories}
                      onSelectedChange={setFilterCategories}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'category'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'category' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Проект"
                      options={projectOptions}
                      selected={filterProjects}
                      onSelectedChange={setFilterProjects}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'project'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Сделка"
                      options={dealOptions}
                      selected={filterDeals}
                      onSelectedChange={setFilterDeals}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'deal'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'deal' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Дата операции</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Синхронизировано</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterSyncedFrom}
                        onChange={(e) => setFilterSyncedFrom(e.target.value)}
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterSyncedTo}
                        onChange={(e) => setFilterSyncedTo(e.target.value)}
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Сумма</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={minAmountFilter}
                        onChange={(e) => setMinAmountFilter(e.target.value)}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={maxAmountFilter}
                        onChange={(e) => setMaxAmountFilter(e.target.value)}
                      />
                    </div>
                  </label>
                </div>
              </aside>
            ) : null}

            <section className="table-content">
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table operations-page__table">
                      <colgroup>
                        <col className="operations-page__colgrid operations-page__colgrid--pf-id" />
                        <col className="operations-page__colgrid operations-page__colgrid--date" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--account-type" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--type" />
                        <col className="operations-page__colgrid operations-page__colgrid--budget-article" />
                        <col className="operations-page__colgrid operations-page__colgrid--category" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--comment" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--modified" />
                        <col className="operations-page__colgrid operations-page__colgrid--synced" />
                      </colgroup>
                      <thead>
                        <tr>
                          {renderSortableHeader('planfact_operation_id', 'ID в ПФ', 'operations-page__th-pf-id')}
                          {renderSortableHeader('operation_date', 'Дата', 'operations-page__th-date')}
                          {renderSortableHeader('account_name', 'Счёт')}
                          {renderSortableHeader('account_type', 'Вал.', 'operations-page__th-account-type')}
                          {renderSortableHeader('contractor_name_pf', 'Контрагент')}
                          {renderSortableHeader('operation_type', 'Тип', 'operations-page__th-type')}
                          {renderSortableHeader('budget_article', 'Статья бюджета', 'operations-page__th-budget-article')}
                          {renderSortableHeader('category_name', 'Статья')}
                          {renderSortableHeader('project_name', 'Проект')}
                          {renderSortableHeader('deal_name', 'Сделка')}
                          {renderSortableHeader('comment', 'Комментарий')}
                          {renderSortableHeader('amount', 'Сумма', 'col-num operations-page__th-sum')}
                          {renderSortableHeader('cash_amount', 'Кэш', 'col-num operations-page__th-sum')}
                          {renderSortableHeader('tax_amount', 'Налог', 'col-num operations-page__th-sum')}
                          {renderSortableHeader('serv_fee_amount', 'Сервисный сбор', 'col-num operations-page__th-sum')}
                          {renderSortableHeader('modified_at', 'Изменено', 'operations-page__th-modified')}
                          {renderSortableHeader('synced_at', 'Синхронизировано', 'operations-page__th-synced')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedOperations.length === 0 ? (
                          <tr>
                            <td colSpan={17} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedOperations.map((row) => (
                            <tr key={row.id}>
                              <td className="operations-page__td-pf-id">{row.planfact_operation_id}</td>
                              <td className="operations-page__td-date">{formatDateShort(row.operation_date)}</td>
                              <td><div className="operations-page__clamp-2">{row.account_name || '—'}</div></td>
                              <td className="operations-page__td-account-type">{row.account_type || '—'}</td>
                              <td><div className="operations-page__clamp-2">{row.contractor_name_pf || '—'}</div></td>
                              <td className="operations-page__td-type">{renderOperationType(row.operation_type)}</td>
                              <td><div className="operations-page__clamp-2">{row.budget_article || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.category_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.project_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.deal_name || '—'}</div></td>
                              <td><div className="operations-page__clamp-2">{row.comment || '—'}</div></td>
                              <td className="col-num operations-page__td-sum">
                                {formatContractSumDisplay(normalizeOperationAmount(row.amount, row.operation_type))}
                              </td>
                              <td className="col-num operations-page__td-sum">{formatOptionalAmount(row.cash_amount)}</td>
                              <td className="col-num operations-page__td-sum">{formatOptionalAmount(row.tax_amount)}</td>
                              <td className="col-num operations-page__td-sum">{formatOptionalAmount(row.serv_fee_amount)}</td>
                              <td className="operations-page__td-modified">{formatDateTimeMsk(row.modified_at)}</td>
                              <td className="operations-page__td-synced">{formatDateTimeMsk(row.synced_at)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer operations-page__table">
                      <colgroup>
                        <col className="operations-page__colgrid operations-page__colgrid--pf-id" />
                        <col className="operations-page__colgrid operations-page__colgrid--date" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--account-type" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--type" />
                        <col className="operations-page__colgrid operations-page__colgrid--budget-article" />
                        <col className="operations-page__colgrid operations-page__colgrid--category" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--text" />
                        <col className="operations-page__colgrid operations-page__colgrid--comment" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--sum" />
                        <col className="operations-page__colgrid operations-page__colgrid--modified" />
                        <col className="operations-page__colgrid operations-page__colgrid--synced" />
                      </colgroup>
                      <tfoot>
                        <tr>
                          <td>Итого: {sortedOperations.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td className="col-num operations-page__td-sum">{formatContractSumDisplay(totalFilteredAmount)}</td>
                          <td className="col-num operations-page__td-sum">{formatContractSumDisplay(totalFilteredCashAmount)}</td>
                          <td className="col-num operations-page__td-sum">{formatContractSumDisplay(totalFilteredTaxAmount)}</td>
                          <td className="col-num operations-page__td-sum">{formatContractSumDisplay(totalFilteredServFeeAmount)}</td>
                          <td />
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default OperationsPage;
